import type { AddOnAnchor } from './addOnModel';

/**
 * What the reader just did, as the anchor a marker host resolves.
 *
 * Every shape here answers to a resolver: `pdfTextAnchors.js` matches a PDF
 * selection by `startPage` and walks to `startPageStartOffset`,
 * `fxlTextAnchors.js` admits an EPUB selection only when it declares
 * `type: 'epub'`, and both EPUB resolvers find the chapter through
 * `locator.href` or `selection.epubChapter`. A shape that drifts from them
 * renders no marker and reports nothing.
 */

/** The Readium locator fields a selection carries, wherever they sit on it. */
interface LocatorFields {
    href?: string;
    type?: string;
    locations?: unknown;
    [key: string]: unknown;
}

interface EpubSelection extends LocatorFields {
    startCss?: string;
}

interface EpubLocation extends LocatorFields {
    selection?: EpubSelection | null;
}

/** `state.currentHighlight`: a live selection, or a stored one the reader tapped. */
interface CurrentHighlight {
    startPage?: number | null;
    startPageStartOffset?: number | null;
    startPageLength?: number | null;
    endPage?: number | null;
    endPageLength?: number | null;
    totalText?: string | null;
    epubChapter?: string | null;
    location?: EpubLocation | null;
    [key: string]: unknown;
}

interface PageRect {
    left: number;
    top: number;
    width: number;
    height: number;
}

const clamp = (value: number): number => Math.min(100, Math.max(0, value));

/**
 * A live selection carries Readium's own `LocatorLocations`, which holds the
 * CSS selector in a Map that a spread keeps and JSON empties. Its serializer
 * flattens that Map back into the keys `createR2Locator` reads, so the anchor
 * lands in the store as data rather than as an object with behaviour.
 */
function plainLocations(locations: unknown): unknown {
    const readium = locations as { serialize?: () => unknown } | null | undefined;

    return typeof readium?.serialize === 'function' ? readium.serialize() : locations;
}

/**
 * @returns The anchor, or null when the selection cannot be placed again
 */
export function textAnchorFromHighlight(
    highlight: CurrentHighlight | null | undefined,
    { isEpub, page }: { isEpub: boolean; page: number }
): AddOnAnchor | null {
    if (!highlight) return null;

    return isEpub ? epubTextAnchor(highlight, page) : pdfTextAnchor(highlight);
}

/** The page-relative offsets the PDF text layer measured. */
function pdfTextAnchor(highlight: CurrentHighlight): AddOnAnchor | null {
    const { startPage, startPageStartOffset, startPageLength, endPage, endPageLength, totalText } = highlight;
    if (startPage == null || startPageStartOffset == null) return null;

    return {
        type: 'text',
        page: startPage,
        selection: {
            type: 'pdf',
            startPage,
            startPageStartOffset,
            startPageLength,
            endPage: endPage ?? startPage,
            // The text layer measures the selection on one page and leaves the
            // end unmeasured, so the start's length is all there is to give.
            endPageLength: endPageLength ?? startPageLength,
        },
        totalText: totalText ?? '',
    };
}

/**
 * The Readium locator says which chapter, the CSS selectors say where in it.
 * Both are needed: a locator alone lands on a position that moves with the
 * font size, and selectors alone name no document to search.
 */
function epubTextAnchor(highlight: CurrentHighlight, page: number): AddOnAnchor | null {
    const location = highlight.location;
    const selection = location?.selection;
    if (!selection?.startCss) return null;

    // A live selection spreads the locator beside the selectors; a stored one,
    // rebuilt from what the host holds, keeps it inside them.
    const locatorFields: LocatorFields = location?.href ? location : selection;
    const href = locatorFields.href ?? highlight.epubChapter;
    if (!href) return null;

    const locator = { href, type: locatorFields.type, locations: plainLocations(locatorFields.locations) };
    return {
        type: 'text',
        page,
        locator,
        selection: { type: 'epub', epubChapter: href, epubR2Location: selection },
    };
}

/**
 * A tap on the page as a share of it, so the marker keeps its place at any
 * zoom or screen. A tap past the page belongs to its edge.
 */
export function pointAnchorFromPageClick({
    clientX,
    clientY,
    pageRect,
    page,
    href,
}: {
    clientX: number;
    clientY: number;
    pageRect: PageRect;
    page: number;
    href?: string;
}): AddOnAnchor {
    const anchor: AddOnAnchor = {
        type: 'point',
        page,
        point: {
            x: clamp(((clientX - pageRect.left) / pageRect.width) * 100),
            y: clamp(((clientY - pageRect.top) / pageRect.height) * 100),
        },
    };

    return href ? { ...anchor, locator: { href } } : anchor;
}

export function pageAnchorFor({ page, href }: { page: number; href?: string }): AddOnAnchor {
    return href ? { type: 'page', page, locator: { href } } : { type: 'page', page };
}

/** A piece still waiting for the reader to place it, which draws no marker. */
export function isPendingAnchor(anchor: AddOnAnchor | null | undefined): boolean {
    return Boolean(anchor?.pending);
}

/** The id the draft carries on the page, before the piece it will become has one of its own. */
export const PENDING_ADD_ON_ID = 'pending';
